import type { PlayInfo } from '@crowdset/contracts';
import {
  ArrowsClockwise,
  ArrowSquareIn,
  Browsers,
  PictureInPicture,
  Headphones,
  Play,
  SpeakerHigh,
  SpeakerLow,
  SpeakerSlash,
  WarningCircle,
} from '@phosphor-icons/react';
import { Slider } from 'radix-ui';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { usePrefs } from '../../lib/prefs';
import { useSession } from '../../lib/queries';
import { FakeAdapter } from '../../player/fake';
import { PlaybackSync, type SyncStatus } from '../../player/sync';
import { playerErrorMessage, type PlayerAdapter } from '../../player/types';
import { YouTubeAdapter } from '../../player/youtube';
import { clock, localNow } from '../../room/clock';
import { useConnection } from '../../room/context';
import { newRequestId } from '../../room/connection';
import { useRoomStore } from '../../room/store';
import { Button, cx, formatDuration } from '../../ui/kit';

const STATUS_COPY: Record<SyncStatus, { label: string; tone: 'ok' | 'warn' | 'muted' | 'danger' }> =
  {
    idle: { label: 'Nothing playing', tone: 'muted' },
    'needs-consent': { label: 'Playback not enabled', tone: 'warn' },
    loading: { label: 'Loading', tone: 'muted' },
    waiting: { label: 'Starting shortly', tone: 'muted' },
    synced: { label: 'In sync with the room', tone: 'ok' },
    buffering: { label: 'Buffering', tone: 'muted' },
    unsynced: { label: 'Listening on your own', tone: 'warn' },
    blocked: { label: 'Your browser blocked playback', tone: 'warn' },
    suspended: { label: 'Paused while the tab was hidden', tone: 'warn' },
    error: { label: 'This video will not play here', tone: 'danger' },
    finished: { label: 'Waiting for the next track', tone: 'muted' },
  };

/** One player per page. The embed is never covered by other UI. */
export function RoomPlayer({ compact = false }: { compact?: boolean }) {
  const session = useSession().data!;
  const conn = useConnection();
  const playback = useRoomStore((s) => s.data?.playback ?? null);
  const play = useRoomStore((s) => s.data?.play ?? null);
  const volume = usePrefs((p) => p.volume);
  const muted = usePrefs((p) => p.muted);
  const setVolume = usePrefs((p) => p.setVolume);
  const setMuted = usePrefs((p) => p.setMuted);
  const hostRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [mountError, setMountError] = useState<string | null>(null);

  const bgPlayback = usePrefs((p) => p.backgroundPlayback);
  // Read inside long-lived callbacks without re-creating the sync engine.
  const keepAliveRef = useRef({ bg: bgPlayback, pip: false });
  keepAliveRef.current.bg = bgPlayback;
  const [pipWin, setPipWin] = useState<Window | null>(null);
  const canPopOut = typeof window !== 'undefined' && 'documentPictureInPicture' in window;

  const { adapter: initialAdapter, sync } = useMemo(() => {
    const adapter: PlayerAdapter =
      session.mediaMode === 'fake' ? new FakeAdapter() : new YouTubeAdapter();
    const sync = new PlaybackSync({
      adapter,
      serverNow: () => clock.serverNow(),
      localNow,
      // "Visible" = the player is on screen (this tab, or the pop-out window), or the listener
      // opted into background playback.
      isVisible: () =>
        document.visibilityState === 'visible' ||
        keepAliveRef.current.pip ||
        keepAliveRef.current.bg,
      report: (kind, playId, errorCode) =>
        void conn.command('playback.report', {
          requestId: newRequestId(),
          playId,
          kind,
          ...(errorCode !== undefined ? { errorCode } : {}),
        }),
    });
    return { adapter, sync };
  }, [session.mediaMode, conn]);
  // The player currently in use (the pop-out swaps in a new one for YouTube).
  const [adapter, setAdapter] = useState<PlayerAdapter>(initialAdapter);
  useEffect(() => setAdapter(initialAdapter), [initialAdapter]);

  const view = useSyncExternalStore(sync.subscribe, sync.getView);

  const events = useMemo(
    () => ({
      onReady: () => {
        sync.adapter.setVolume(usePrefs.getState().volume);
        sync.adapter.setMuted(usePrefs.getState().muted);
      },
      onState: (s: Parameters<typeof sync.onState>[0]) => sync.onState(s),
      onError: (c: number) => sync.onError(c),
      onAutoplayBlocked: () => sync.onAutoplayBlocked(),
    }),
    [sync],
  );

  // mount the in-page adapter (the pop-out mounts its own)
  useEffect(() => {
    let cancelled = false;
    initialAdapter
      .mount(hostRef.current!, events)
      .catch((e: Error) => !cancelled && setMountError(e.message));
    (window as unknown as { __crowdsetPlayer?: unknown }).__crowdsetPlayer = {
      get adapter() {
        return sync.adapter;
      },
      sync,
    };
    return () => {
      cancelled = true;
      initialAdapter.destroy();
    };
  }, [initialAdapter, sync, events]);

  /** Move the player into a small always-on-top window so it keeps playing while you use other tabs. */
  const popOut = async () => {
    const dpip = (
      window as unknown as {
        documentPictureInPicture?: {
          requestWindow(o: { width: number; height: number }): Promise<Window>;
        };
      }
    ).documentPictureInPicture;
    if (!dpip || pipWin) return;
    let win: Window;
    try {
      win = await dpip.requestWindow({ width: 480, height: 270 });
    } catch {
      useRoomStore.getState().toast('Your browser did not allow a pop-out window here.', 'warn');
      return;
    }
    keepAliveRef.current.pip = true;
    win.document.title = play?.title ? `${play.title} · CrowdSet` : 'CrowdSet';
    const body = win.document.body;
    body.style.cssText = 'margin:0;height:100vh;overflow:hidden;background:#0b0b0e';
    if (sync.adapter.kind === 'youtube') {
      // about:blank (the pop-out document) sends no Referer, which YouTube rejects (error 153),
      // so the player lives in a same-origin page inside it.
      const frame = win.document.createElement('iframe');
      frame.src = `${window.location.origin}/player-frame.html`;
      frame.title = 'YouTube player';
      frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      frame.style.cssText = 'border:0;width:100%;height:100%;display:block';
      body.append(frame);
      await new Promise((r) => frame.addEventListener('load', r, { once: true }));
      const fw = frame.contentWindow as Window & { ytReady?: Promise<never> };
      const next = new YouTubeAdapter(fw);
      await next.mount(fw.document.getElementById('host')!, events);
      const old = sync.adapter;
      sync.replaceAdapter(next);
      if (old !== next) old.stop();
      setAdapter(next);
    } else {
      // Demo player: render the same React view into the pop-out with the app's styles.
      for (const node of document.head.querySelectorAll('style, link[rel="stylesheet"]')) {
        win.document.head.append(node.cloneNode(true));
      }
    }
    win.addEventListener('pagehide', () => bringBack(), { once: true });
    setPipWin(win);
  };

  /** Pop-out closed (by the user or the button): put the player back in the page. */
  const bringBack = () => {
    keepAliveRef.current.pip = false;
    setPipWin((w) => {
      if (w && !w.closed) w.close();
      return null;
    });
    if (sync.adapter !== initialAdapter) {
      // The in-page player was only stopped while popped out; hand playback back to it.
      const popped = sync.adapter;
      sync.replaceAdapter(initialAdapter);
      popped.destroy();
      setAdapter(initialAdapter);
    }
  };

  // feed authoritative playback
  useEffect(() => {
    sync.update(playback);
  }, [sync, playback?.playId, playback?.status, playback?.startsAtMs, playback?.playbackRevision]); // eslint-disable-line react-hooks/exhaustive-deps

  // tick: start scheduled plays, drift checks (~5s)
  useEffect(() => {
    const t = setInterval(() => sync.tick(), 1000);
    const onVis = () =>
      sync.onVisibility(
        document.visibilityState === 'visible' ||
          keepAliveRef.current.pip ||
          keepAliveRef.current.bg,
      );
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [sync]);

  // only start automated playback when the player is sufficiently visible
  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([e]) => sync.setInView((e?.intersectionRatio ?? 0) >= 0.5),
      { threshold: [0, 0.5, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [sync]);

  useEffect(() => adapter.setVolume(volume), [adapter, volume]);
  useEffect(() => adapter.setMuted(muted), [adapter, muted]);
  // Close the pop-out if the room view goes away.
  useEffect(() => () => pipWin?.close(), [pipWin]);

  const enable = () => {
    usePrefs.getState().setPlaybackConsent(true);
    sync.enable();
  };

  // Consent can come from an earlier visit, from Enable playback, or from clicking Join DJ queue
  // (a user gesture). Apply it once; if the browser still blocks autoplay, the player reports it and
  // the Enable button returns (no retry loop).
  const consent = usePrefs((p) => p.playbackConsent);
  useEffect(() => {
    if (consent && !sync.hasConsent) sync.enable();
  }, [consent, sync]);

  const idle = !playback || playback.status === 'idle';
  const st = STATUS_COPY[view.status];
  const needsGesture = view.status === 'needs-consent' || view.status === 'blocked';
  const canResync = ['unsynced', 'suspended', 'finished', 'error'].includes(view.status) && !idle;

  return (
    <div className="flex w-full flex-col gap-2">
      <div
        ref={frameRef}
        className={cx(
          'relative w-full overflow-hidden rounded-2xl border border-white/10 bg-ink-950 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)]',
          'aspect-video',
          !compact && 'lg:min-w-[480px]',
        )}
      >
        {/* The embed host. Nothing is layered over it while a video is shown. */}
        <div
          ref={hostRef}
          className={cx(
            'absolute inset-0 [&>iframe]:h-full [&>iframe]:w-full',
            (idle || adapter.kind === 'fake' || pipWin) && 'hidden',
          )}
        />
        {adapter.kind === 'fake' &&
          !idle &&
          (pipWin ? (
            createPortal(
              <div className="relative h-screen w-screen">
                <FakeScreen adapter={adapter as FakeAdapter} play={play} />
              </div>,
              pipWin.document.body,
            )
          ) : (
            <FakeScreen adapter={adapter as FakeAdapter} play={play} />
          ))}
        {pipWin && (
          <div className="absolute inset-0 flex flex-col items-start justify-end gap-3 bg-ink-950 p-6">
            <PictureInPicture size={26} className="text-accent" aria-hidden />
            <p className="text-lg font-semibold tracking-tight">Playing in the pop-out window</p>
            <p className="max-w-[46ch] text-sm text-fg-muted">
              The player keeps going while you use other tabs. Close the pop-out or press Bring back
              to return it here.
            </p>
            <Button size="sm" variant="outline" onClick={bringBack}>
              <ArrowSquareIn size={14} weight="bold" aria-hidden /> Bring back
            </Button>
          </div>
        )}
        {idle && <IdleScreen />}
        {mountError && (
          <div className="absolute inset-0 flex flex-col items-start justify-end gap-2 bg-ink-950 p-5 text-sm">
            <WarningCircle size={22} className="text-warn" aria-hidden />
            <p className="max-w-[48ch] text-fg-muted">{mountError}</p>
          </div>
        )}
      </div>

      {/* Local controls — they only affect you. */}
      <div
        className="flex items-center gap-2 rounded-xl border border-white/[0.07] bg-ink-900/85 px-2 py-1.5 backdrop-blur"
        role="group"
        aria-label="Your playback (only affects you)"
      >
        {(needsGesture && !idle) || (idle && !consent) ? (
          <Button variant="primary" size="sm" onClick={enable} className="animate-none">
            <Play size={14} weight="fill" aria-hidden /> Enable playback
          </Button>
        ) : (
          <Button
            variant={canResync ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => sync.syncToRoom()}
            disabled={idle}
            aria-label="Sync to room"
          >
            <ArrowsClockwise size={14} weight="bold" aria-hidden /> Sync to room
          </Button>
        )}
        <span
          className={cx(
            'flex min-w-0 flex-1 items-center gap-1.5 text-xs',
            st.tone === 'ok' && 'text-ok',
            st.tone === 'warn' && 'text-warn',
            st.tone === 'danger' && 'text-[#f3a3a4]',
            st.tone === 'muted' && 'text-fg-subtle',
          )}
          role="status"
          aria-live="polite"
        >
          <span
            className={cx(
              'h-1.5 w-1.5 shrink-0 rounded-full bg-current',
              view.status === 'synced' && 'cs-pulse-dot',
            )}
            aria-hidden
          />
          <span className="truncate">
            {view.status === 'synced' ? (
              <>
                In sync
                <span className={compact ? 'hidden sm:inline' : undefined}> with the room</span>
              </>
            ) : (
              st.label
            )}
          </span>
          {view.status === 'synced' && view.driftMs !== null && (
            <span className="hidden font-mono text-fg-subtle sm:inline">
              ({(view.driftMs / 1000).toFixed(1)}s)
            </span>
          )}
        </span>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {canPopOut && (
            <button
              className="flex h-8 w-8 items-center justify-center rounded-lg text-fg-muted hover:bg-white/[0.07] hover:text-fg"
              onClick={() => (pipWin ? bringBack() : void popOut())}
              aria-pressed={!!pipWin}
              aria-label={
                pipWin
                  ? 'Bring the player back into the page'
                  : 'Pop out player (keeps playing in other tabs)'
              }
              title={
                pipWin ? 'Bring back' : 'Pop out player — keeps playing while you use other tabs'
              }
            >
              {pipWin ? <ArrowSquareIn size={16} /> : <PictureInPicture size={16} />}
            </button>
          )}
          <button
            className={cx(
              'flex h-8 w-8 items-center justify-center rounded-lg hover:bg-white/[0.07]',
              bgPlayback ? 'text-accent' : 'text-fg-muted hover:text-fg',
            )}
            onClick={() => {
              usePrefs.getState().setBackgroundPlayback(!bgPlayback);
              // Turning it on while paused-for-hidden resumes following straight away.
              if (!bgPlayback && view.status === 'suspended') sync.syncToRoom();
            }}
            aria-pressed={bgPlayback}
            aria-label="Keep playing when I switch tabs"
            title={
              bgPlayback
                ? 'Keeps playing when you switch tabs (on)'
                : 'Keep playing when I switch tabs (off)'
            }
          >
            <Browsers size={16} weight={bgPlayback ? 'fill' : 'regular'} />
          </button>
          <button
            className="flex h-8 w-8 items-center justify-center rounded-lg text-fg-muted hover:bg-white/[0.07] hover:text-fg"
            onClick={() => setMuted(!muted)}
            aria-pressed={muted}
            aria-label={muted ? 'Unmute (only you)' : 'Mute (only you)'}
          >
            {muted || volume === 0 ? (
              <SpeakerSlash size={16} />
            ) : volume < 50 ? (
              <SpeakerLow size={16} />
            ) : (
              <SpeakerHigh size={16} />
            )}
          </button>
          <Slider.Root
            className="relative flex h-8 w-16 touch-none sm:w-24 select-none items-center"
            value={[muted ? 0 : volume]}
            max={100}
            step={1}
            onValueChange={([v]) => {
              setVolume(v ?? 0);
              if ((v ?? 0) > 0 && muted) setMuted(false);
            }}
            aria-label="Volume (only you)"
          >
            <Slider.Track className="relative h-1 grow rounded-full bg-white/15">
              <Slider.Range className="absolute h-full rounded-full bg-fg-muted" />
            </Slider.Track>
            <Slider.Thumb
              className="block h-3.5 w-3.5 rounded-full bg-fg shadow focus-visible:outline-2 focus-visible:outline-accent"
              aria-label="Volume"
            />
          </Slider.Root>
        </div>
      </div>
      {view.status === 'error' && view.errorCode !== null && (
        <p className="text-xs leading-relaxed text-fg-muted" role="alert">
          {playerErrorMessage(view.errorCode)} Others in the room may still be able to play it; the
          room keeps going.
        </p>
      )}
      {view.status === 'blocked' && (
        <p className="text-xs text-fg-muted">
          Browsers only allow sound after you interact with the page. Press Enable playback.
        </p>
      )}
      {view.status === 'suspended' && (
        <p className="text-xs text-fg-muted">
          Playback paused while the tab was hidden. Press Sync to room to rejoin where the room is
          now.{' '}
          <button
            className="text-accent underline-offset-2 hover:underline"
            onClick={() => {
              usePrefs.getState().setBackgroundPlayback(true);
              sync.syncToRoom();
            }}
          >
            Resume and keep playing in background tabs
          </button>
          {canPopOut && (
            <>
              {' '}
              or{' '}
              <button
                className="text-accent underline-offset-2 hover:underline"
                onClick={() => void popOut()}
              >
                pop out the player
              </button>
            </>
          )}
          .
        </p>
      )}
    </div>
  );
}

function IdleScreen() {
  return (
    <div className="absolute inset-0 flex flex-col items-start justify-end gap-2 bg-[radial-gradient(120%_90%_at_20%_110%,rgba(95,183,201,0.14),transparent_60%)] p-6">
      <Headphones size={28} className="text-accent" aria-hidden />
      <p className="text-lg font-semibold tracking-tight">The booth is empty</p>
      <p className="max-w-[44ch] text-sm text-fg-muted">
        Nobody is DJing. Join the queue with an active playlist and your first track starts right
        away.
      </p>
    </div>
  );
}

/** Visible, labelled demo player. Never presented as YouTube. */
function FakeScreen({ adapter, play }: { adapter: FakeAdapter; play: PlayInfo | null }) {
  const [, force] = useState(0);
  useEffect(() => {
    const un = adapter.subscribe(() => force((n) => n + 1));
    const t = setInterval(() => force((n) => n + 1), 250);
    return () => {
      un();
      clearInterval(t);
    };
  }, [adapter]);
  const pos = adapter.currentTimeMs();
  const dur = adapter.duration || play?.durationMs || 1;
  const state = adapter.state();
  const bars = 28;
  return (
    <div
      className="absolute inset-0 flex flex-col justify-between bg-[linear-gradient(135deg,#15151b,#1d1a24)] p-5"
      data-testid="fake-player"
      data-state={state}
      data-position-ms={Math.round(pos)}
    >
      <div className="flex items-center gap-2">
        <span className="rounded-md border border-warn/40 bg-warn/10 px-2 py-0.5 text-[11px] font-medium text-warn">
          Demo media — not YouTube
        </span>
        <span className="font-mono text-[11px] text-fg-subtle">{adapter.videoId}</span>
      </div>
      <div className="flex h-24 items-end gap-1" aria-hidden="true">
        {Array.from({ length: bars }, (_, i) => {
          const h = state === 'playing' ? 20 + Math.abs(Math.sin(pos / 380 + i * 0.7)) * 80 : 12;
          return (
            <span
              key={i}
              className="flex-1 rounded-sm bg-accent/60 transition-[height] duration-200"
              style={{ height: `${h}%` }}
            />
          );
        })}
      </div>
      <div>
        <p className="truncate text-base font-semibold tracking-tight">
          {play?.title ?? 'Test signal'}
        </p>
        <div className="mt-2 flex items-center gap-3 font-mono text-xs text-fg-muted">
          <span>{formatDuration(pos)}</span>
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full bg-fg-muted"
              style={{ width: `${Math.min(100, (pos / dur) * 100)}%` }}
            />
          </div>
          <span>{formatDuration(dur)}</span>
          <span className="uppercase tracking-wider text-fg-subtle">{state}</span>
        </div>
        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => (state === 'playing' ? adapter.pause() : adapter.play())}
          >
            {state === 'playing' ? 'Pause (local)' : 'Play (local)'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => adapter.seek(adapter.currentTimeMs() + 15_000)}
          >
            +15s (local)
          </Button>
        </div>
      </div>
    </div>
  );
}
